import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { showErrorToast, showSuccessToast } from "../utils/toaster";
import logo from "../Images/buillogo6.png";
import { baseurl } from "../config/url";
import ReggenerateInvoice from "../utils/ReggenerateInvoice"; // uses new signature

const PaymentPage = () => {
  const baseAmount = 10000; // ₹10,000
  const gstRate = 0.18;
  const totalAmount = Math.round(baseAmount * (1 + gstRate)); // 11800

  const navigate = useNavigate();
  const location = useLocation();
  const { sellerId } = location.state || {};

  const [sellerDetails, setSellerDetails] = useState(null);
  const [timeLeft, setTimeLeft] = useState(880);
  const [orderDetails, setOrderDetails] = useState(null);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [offlinePayment, setOfflinePayment] = useState({
    method: "",
    amount: 10000,
    date: "",
    bankName: "",
    transactionId: "",
    ifscCode: "",
    accountNumber: "",
    applyCode: "",
    discountApplied: false,
  });

  // Fetch seller details
  useEffect(() => {
    const stateSellerId = location?.state?.sellerId;
    const storedSellerId = localStorage.getItem("sellerId");
    const sellerIdToUse = stateSellerId || storedSellerId;

    if (!sellerIdToUse || sellerIdToUse === "undefined" || sellerIdToUse === "null") {
      showErrorToast("Seller ID is missing");
      return;
    }
    axios
      .get(`${baseurl}/api/seller/seller-by-id/${sellerIdToUse}`)
      .then((res) => setSellerDetails(res.data))
      .catch(() => showErrorToast("Unable to fetch seller details"));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r < 10 ? "0" : ""}${r}`;
    };

  const updatePaymentStatus = async (data) => {
    const sid = localStorage.getItem("sellerId");
    if (!sid) {
      showErrorToast("Seller ID is required");
      return;
    }
    try {
      const paymentStatusRequest = axios.put(
        `${baseurl}/api/seller/updateSeller/${sid}`,
        { isPaymentDone: true },
        { headers: { "Content-Type": "application/json" } }
      );
      const savePaymentRequest = axios.post(
        `${baseurl}/api/payment/seller-payment-store-todb`,
        {
          sellerId: sid,
          orderCreationId: data.orderCreationId,
          razorpayPaymentId: data.razorpayPaymentId,
          razorpayOrderId: data.razorpayOrderId,
          razorpaySignature: data.razorpaySignature,
          amount: baseAmount,
          currency: "INR",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      await Promise.all([paymentStatusRequest, savePaymentRequest]);

      // 🔽 Generate FINAL INVOICE (after payment)
      if (sellerDetails) {
        ReggenerateInvoice(sellerDetails, {
          isPaid: true,
          paymentInfo: {
            orderId: data.orderCreationId,
            paymentId: data.razorpayPaymentId,
            method: "Razorpay",
          },
        });
      }

      showSuccessToast("Payment updated and details saved successfully!");
      navigate("/");
    } catch (error) {
      console.error("Error:", error);
      showErrorToast(error.response?.data?.message || "Something went wrong");
    }
  };

  // Razorpay payment
  const displayRazorpay = async (amount) => {
    try {
      const response = await axios.post(`${baseurl}/api/payment/create-payment-order`, { amount });
      if (!response.data.success) {
        showErrorToast(response.data.message || "Failed to create order");
        return;
      }
      const { order, key } = response.data;
      const { id: order_id, currency } = order;

      const options = {
        key,
        amount,
        currency,
        name: "BuildPro",
        description: `Seller Registration: ₹${baseAmount} + 18% GST = ₹${totalAmount}`,
        image: logo,
        order_id,
        handler: async function (response) {
          const { razorpay_payment_id, razorpay_order_id, razorpay_signature } = response;
          const data = {
            orderCreationId: order_id,
            razorpayPaymentId: razorpay_payment_id,
            razorpayOrderId: razorpay_order_id,
            razorpaySignature: razorpay_signature,
          };
          if (data.razorpayPaymentId) updatePaymentStatus(data);
          showSuccessToast("Payment Successful!");
        },
        notes: { address: "Customer Address" },
        theme: { color: "#F37254" },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Error in Razorpay payment:", error);
      showErrorToast("Payment failed. Please try again later.");
    }
  };

  // BEFORE payment: Proforma invoice / download
  const handleDownloadInvoice = () => {
    if (!sellerDetails) {
      showErrorToast("Seller details not found.");
      return;
    }

    const isOffline = offlinePayment.discountApplied;
    const invoiceDetails = {
      ...sellerDetails,
      discountApplied: isOffline,
      couponApplied: isOffline ? offlinePayment.applyCode : null,
      couponPercentage: offlinePayment.couponPercentage,
    };

    ReggenerateInvoice(invoiceDetails, { isPaid: false }); // 🔴 PROFORMA
    showSuccessToast("Proforma invoice generated!");
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100 py-10">
      <div className="w-full max-w-2xl bg-white shadow-lg rounded-lg p-8">
        <h1 className="text-3xl font-semibold text-center text-indigo-600">
          OGCS Private Limited
        </h1>

        {/* Billing Section */}
        <div className="mt-8">
          <h2 className="text-2xl font-semibold text-gray-800">Billing Details</h2>
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
            <div className="flex justify-between mb-2">
              <p className="text-md text-gray-700">Base Fee</p>
              <p className="text-md text-gray-900">INR {baseAmount}</p>
            </div>
            <div className="flex justify-between mb-2">
              <p className="text-md text-gray-700">GST (18%)</p>
              <p className="text-md text-gray-900">INR {Math.round(baseAmount * gstRate)}</p>
            </div>
            <div className="flex justify-between mb-4 border-t pt-2 font-semibold text-lg">
              <p>Total Payable</p>
              <p>INR {totalAmount}</p>
            </div>
            <div className="flex justify-between mb-2">
              <p className="text-md text-gray-700">Transaction Timeout</p>
              <p className="text-md text-gray-900">{formatTime(timeLeft)}</p>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Please complete the payment within the time limit to proceed with registration.
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-8">
          <button
            onClick={() => displayRazorpay(totalAmount)}
            className="w-full py-3 text-white font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700"
          >
            Proceed to Pay INR 10000
          </button>
        </div>

        <div className="mt-8">
          <button
            onClick={handleDownloadInvoice}
            className={`w-full py-3 text-white font-semibold rounded-lg ${
              sellerDetails ? "bg-indigo-600 hover:bg-indigo-700" : "bg-gray-400 cursor-not-allowed"
            }`}
            disabled={!sellerDetails}
          >
            Download Proforma Invoice
          </button>

          <button
            onClick={() => setShowOfflineModal(true)}
            className="w-full py-3 text-white font-semibold rounded-lg bg-yellow-600 hover:bg-yellow-700 mt-4"
          >
            Pay Offline (NEFT / RTGS / Cheque)
          </button>
        </div>
      </div>

      {/* Offline Modal */}
      {showOfflineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded shadow-lg w-full sm:w-3/4 md:w-1/2">
            <h2 className="text-xl font-semibold mb-4">Offline Payment</h2>

            {/* Fields */}
            <select
              value={offlinePayment.method}
              onChange={(e) => setOfflinePayment({ ...offlinePayment, method: e.target.value })}
              className="mb-2 p-2 border rounded w-full"
            >
              <option value="">Select Payment Method</option>
              <option value="IMPS">IMPS</option>
              <option value="NEFT">NEFT</option>
              <option value="RTGS">RTGS</option>
              <option value="DD">DD</option>
              <option value="CHEQUE">Cheque</option>
            </select>

            <input
              type="date"
              value={offlinePayment.date}
              onChange={(e) => setOfflinePayment({ ...offlinePayment, date: e.target.value })}
              className="mb-2 p-2 border rounded w-full"
            />
            <input
              type="text"
              placeholder="Bank Name"
              value={offlinePayment.bankName}
              onChange={(e) => setOfflinePayment({ ...offlinePayment, bankName: e.target.value })}
              className="mb-2 p-2 border rounded w-full"
            />
            <input
              type="text"
              placeholder="Transaction ID"
              value={offlinePayment.transactionId}
              onChange={(e) => setOfflinePayment({ ...offlinePayment, transactionId: e.target.value })}
              className="mb-2 p-2 border rounded w-full"
            />
            <input
              type="text"
              placeholder="IFSC Code"
              value={offlinePayment.ifscCode}
              onChange={(e) => setOfflinePayment({ ...offlinePayment, ifscCode: e.target.value })}
              className="mb-2 p-2 border rounded w-full"
            />
            <input
              type="text"
              placeholder="Account Number"
              value={offlinePayment.accountNumber}
              onChange={(e) => setOfflinePayment({ ...offlinePayment, accountNumber: e.target.value })}
              className="mb-4 p-2 border rounded w-full"
            />
            <input
              type="text"
              placeholder="Apply Code"
              value={offlinePayment.applyCode}
              onChange={(e) =>
                setOfflinePayment({ ...offlinePayment, applyCode: e.target.value.toUpperCase() })
              }
              className="mb-2 p-2 border rounded w-full"
            />

            <button
              className={`mb-4 px-4 py-2 rounded w-full ${
                offlinePayment.discountApplied ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
              disabled={offlinePayment.discountApplied}
              onClick={async () => {
                const code = offlinePayment.applyCode.trim().toUpperCase();
                const sid = localStorage.getItem("sellerId");
                if (!code) {
                  showErrorToast("Please enter a coupon code.");
                  return;
                }
                try {
                  const res = await axios.post(`${baseurl}/api/coupons/validate`, { code, sellerId: sid });
                  const discountPercentage = res.data.discountPercentage;
                  const originalAmount = 10000;
                  const discountAmount = (originalAmount * discountPercentage) / 100;
                  const newAmount = originalAmount - discountAmount;

                  setOfflinePayment((prev) => ({
                    ...prev,
                    amount: newAmount,
                    discountApplied: true,
                    couponPercentage: discountPercentage,
                  }));
                  showSuccessToast(`Coupon applied! ₹${discountAmount} off. You pay ₹${newAmount}.`);
                } catch (err) {
                  showErrorToast(err.response?.data?.message || "Invalid or expired coupon.");
                }
              }}
            >
              Apply
            </button>

            <p className="mb-2 text-green-600 font-semibold">
              Amount To Pay: ₹{offlinePayment.amount.toFixed(0)}
            </p>

            <button
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full"
              onClick={async () => {
                const sid = localStorage.getItem("sellerId");
                if (!sid) {
                  showErrorToast("Seller ID not found.");
                  return;
                }

                const {
                  method, amount, date, bankName, transactionId, ifscCode, accountNumber, applyCode, discountApplied
                } = offlinePayment;

                if (!method || !amount || !date || !bankName || !transactionId || !ifscCode || !accountNumber) {
                  showErrorToast("Please fill all required fields.");
                  return;
                }

                try {
                  // 1) Save offline payment
                  await axios.post(`${baseurl}/api/payment/other-payment`, {
                    ...offlinePayment,
                    productIds: [sid],
                    sellerId: sid,
                  });

                  // 2) Mark coupon used (if any)
                  if (discountApplied && applyCode) {
                    await axios.post(`${baseurl}/api/coupons/mark-used`, {
                      code: applyCode.trim().toUpperCase(),
                      sellerId: sid,
                    });
                  }

                  // 3) Generate FINAL INVOICE (after payment)
                  if (sellerDetails) {
                    ReggenerateInvoice(
                      {
                        ...sellerDetails,
                        discountApplied,
                        couponApplied: applyCode,
                        couponPercentage: offlinePayment.couponPercentage,
                      },
                      {
                        isPaid: true,
                        paymentInfo: {
                          orderId: `OFF-${Date.now()}`,
                          paymentId: transactionId,
                          method: "Offline",
                        },
                      }
                    );
                  }

                  showSuccessToast("Offline Payment submitted successfully!");
                  setShowOfflineModal(false);
                  setTimeout(() => navigate("/"), 1200);
                } catch (err) {
                  console.error(err);
                  showErrorToast("Failed to record offline payment or mark coupon as used.");
                }
              }}
            >
              Submit Offline Payment
            </button>

            <button className="mt-2 text-gray-600 hover:underline w-full" onClick={() => setShowOfflineModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;